import { apiClient } from "./client";

export type TipoScheda = "installazione" | "sopralluogo" | "intervento" | "attivazione";

/** Scheda normalizzata del calendario unificato (4 tipi in un formato comune). */
export interface SchedaCalendario {
  id: string;
  tipo: TipoScheda;
  titolo: string;
  data: string | null;
  cliente: string | null;
  cliente_id: string | null;
  stato: string | null;
  /**
   * L'orario dell'appuntamento, già composto dal backend: "09:30 – 12:00",
   * "dalle 09:30", oppure `null` se non è stato indicato.
   *
   * Arriva composto e non come due estremità perché la regola per metterle in
   * fila dev'essere una sola per tutti e tre i moduli che hanno un orario. I
   * sopralluoghi non ne hanno: per loro è sempre `null`.
   */
  orario: string | null;
  /** true se l'utente può modificare questa scheda: decide cosa è trascinabile. */
  can_edit: boolean;
  /**
   * Perché la scheda non è trascinabile, quando il motivo è una regola di
   * dominio e non un permesso (oggi: intervento sincronizzato da Odoo).
   * Valorizzato solo in quel caso: serve a spiegare il blocco invece di
   * lasciare che il gesto semplicemente non risponda.
   */
  motivo_blocco: string | null;
  /**
   * Campo data che il trascinamento deve aggiornare. Lo dichiara il backend
   * perché per alcuni moduli la data mostrata è una cascata (attivazioni,
   * interventi): il drop scrive il campo che si sta vedendo.
   */
  campo_data: string;
  /**
   * L'attività risulta svolta. Calcolata dal backend con gli stessi predicati
   * che reggono i badge "In ritardo"/"Da completare" delle quattro liste
   * (`backend/src/utils/ritardo.ts`): il criterio di "svolta" cambia per
   * modulo e non va rifatto qui.
   */
  completata: boolean;
  /**
   * L'attività è scaduta e non svolta. Non è il contrario di `completata`:
   * una scheda futura e non svolta non è né l'una né l'altra.
   */
  in_ritardo: boolean;
  /**
   * Chiusa di proposito senza svolgersi: installazione `Rinviata`, attivazione
   * `Cessata`. Il Calendario non le mette icona di stato — non sono completate,
   * ma non chiedono niente a nessuno.
   *
   * Indipendente da `in_ritardo`, che resta quello delle liste: una `Rinviata`
   * scaduta è ancora "In ritardo" nella lista Installazioni.
   */
  chiusa_di_proposito: boolean;
}

/** Rotta API del modulo, per il PATCH di una scheda spostata nel calendario. */
const ENDPOINT_PER_TIPO: Record<TipoScheda, string> = {
  installazione: "/schedule",
  sopralluogo: "/sopralluoghi",
  intervento: "/interventi",
  attivazione: "/attivazioni",
};

/**
 * Sposta una scheda su un'altra data, dal trascinamento nel calendario.
 *
 * Chiama il PATCH del modulo di appartenenza invece di un endpoint aggregato:
 * ogni modulo ha le proprie regole di autorizzazione e il proprio audit log, e
 * passare da lì li mantiene entrambi senza duplicarli. Il campo da scrivere
 * arriva dalla scheda stessa (`campo_data`).
 *
 * `extra` aggiunge altri campi allo stesso PATCH — oggi solo lo stato
 * "Programmata" quando un'installazione "Da programmare" viene trascinata su
 * un giorno (vedi `CalendarioUnificato.handleDragEnd`).
 */
export async function spostaSchedaSuData(
  scheda: SchedaCalendario,
  isoDate: string,
  extra?: Record<string, unknown>
): Promise<void> {
  await apiClient.patch(`${ENDPOINT_PER_TIPO[scheda.tipo]}/${scheda.id}`, {
    [scheda.campo_data]: isoDate,
    ...extra,
  });
}

/**
 * Calendario unificato: installazioni (da installation_schedule) + sopralluoghi
 * + interventi + attivazioni, filtrate lato server sui permessi/scope dell'utente.
 * `tipi` opzionale restringe ai tipi indicati.
 */
export async function getCalendario(opts?: { tipi?: TipoScheda[]; cliente_id?: string }): Promise<SchedaCalendario[]> {
  const params: Record<string, string> = {};
  if (opts?.tipi && opts.tipi.length) params.tipi = opts.tipi.join(",");
  if (opts?.cliente_id) params.cliente_id = opts.cliente_id;
  const { data } = await apiClient.get<{ schede: SchedaCalendario[] }>("/calendario", {
    params: Object.keys(params).length ? params : undefined,
  });
  return data.schede;
}
